import React from 'react'
import Head from 'next/head'
import { useTranslations } from 'next-intl'
import { cn } from '../utils/cn'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { ascii } from '../utils/ascii'
import Header from '../components/layouts/header'
import ASCIIDoc from '../docs/ascii.mdx'
import { useLocalizedUrl } from '../hooks/useLocalizedUrl'

function ASCIITable({ id }: { id: string }) {
  const t = useTranslations('ASCII')
  
  return (
    <div>
      <div className="mb-4 font-mono text-2xl text-gray-800">
        {
          id ? `${ascii[Number(id)].symbol}, ${ascii[Number(id)].description}` : t('tableTitle')
        }
      </div>
      <div className="grid grid-flow-row-dense font-mono text-sm text-gray-700 border-t border-l border-gray-300" style={{
        gridTemplateColumns: 'repeat(auto-fill, minmax(5rem, 1fr))',
        gridAutoRows: 'min-content',
      }}>
        {
          ascii.map((char, i) => {
            const isWord = (i >= 48 && i <= 57) || (i >= 65 && i <= 90) || (i >= 97 && i <= 122)
            const isControl = (i >= 0 && i <= 31) || i === 127
            const currentId = char.code === id
            return (
              <Link href={`/ascii/${i}`} key={char.code} legacyBehavior>
                <a
                  className={cn('px-4 py-2 text-center border-b border-r cursor-pointer char border-gray-300 text-gray-700', {
                    'bg-orange-100': !currentId && isWord,
                    'bg-red-100': !currentId && isControl,
                    'bg-blue-100': !currentId && !isWord && !isControl,
                    'bg-white': char.code === id
                  })}
                >
                  <div className={cn('mb-2 text-base', {
                    'text-orange-500': isWord,
                    'text-red-500': isControl,
                    'text-blue-500': !isWord && !isControl
                  })}>
                    {char.symbol}
                  </div>
                  <div className="mb-2">
                    0x{Number(char.code).toString(16).padStart(2, '0')}
                  </div>
                  <div>
                    {char.code}
                  </div>
                </a>
              </Link>
            )
          })
        }
      </div>
    </div>
  )
}

function ASCII() {
  const { query } = useRouter()
  const t = useTranslations('ASCII')
  const localizedUrl = useLocalizedUrl('/ascii')

  return (
    <div className="min-h-screen">
      <Head>
        <title>{t('pageTitle')}</title>
        <meta name="description" content={t('metaDescription')} />
        <meta property="og:title" content={t('pageTitle')} />
        <meta property="og:description" content={t('metaDescription')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={localizedUrl} />
        <meta property="og:image" content="https://devtool.tech/thumbnail/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('pageTitle')} />
        <meta name="twitter:description" content={t('metaDescription')} />
        <meta name="twitter:image" content="https://devtool.tech/thumbnail/og-image.png" />
        <link rel="canonical" href={localizedUrl} />
      </Head>
      <Header title={t('headerTitle')} />
      <div className="container mx-auto px-4 py-8">
        <ASCIITable id={query.id as string} />
        <ASCIIDoc />
      </div>
    </div>
  )
}

export default ASCII

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      messages: (await import(`../messages/${locale}.json`)).default
    }
  }
}
